// An agent's email as a draft in the operator's own Gmail, made from the browser at
// the click: the compose grant lives in this tab only, so no stored token and no
// agent can write to anyone's mail. See docs/Y2026-Q4/design-gmail-reply-drafts.md.
import { emailAddresses } from "../utils/gmailLinks";
import { GMAIL_API_BASE, gmailFetch, type OnGmailRetry } from "./gmail";
import {
  ensureGsiLoaded,
  refreshGoogleAccessToken,
  requestGoogleAccessToken,
} from "./google-auth";
import { GOOGLE_GMAIL_COMPOSE_SCOPE, GOOGLE_GMAIL_SCOPE } from "./google-config";

// Read to find the operator's copy of the thread, compose to write the draft.
const DRAFT_SCOPES = [GOOGLE_GMAIL_SCOPE, GOOGLE_GMAIL_COMPOSE_SCOPE];

// Message-IDs to try, newest first, before deciding the operator was not on it.
const MAX_THREAD_LOOKUPS = 10;

/** ``POST /uploads/reply_context``: who a reply copies, its subject and headers. */
export interface ReplyContext {
  upload_id: string;
  subject: string;
  participants: string[];
  in_reply_to: string;
  references: string[];
  message_ids: string[];
  /** Each connected mailbox's own Gmail thread id for it. */
  mailbox_threads: { account: string; thread_id: string }[];
}

export interface DraftFields {
  to: string;
  cc: string;
  subject: string;
  body: string;
}

/** Where the draft landed: on the operator's copy of the conversation, or as a new
 * message because there was no conversation, they were not on it, it had no
 * Message-IDs to find it by, or Gmail split it off (a changed subject does). */
export type DraftPlacement =
  | "thread"
  | "new"
  | "not_on_thread"
  | "no_ids"
  | "split";

export interface SavedGmailDraft {
  draftId: string;
  messageId: string;
  account: string;
  placement: DraftPlacement;
}

class GmailStatusError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

const lower = (addresses: string[]): Set<string> =>
  new Set(addresses.map((a) => a.toLowerCase()));

/** The Cc a reply starts with: reply-all on the thread's latest message, minus the
 * people on To and the operator. */
export const copyLine = (
  participants: string[],
  to: string,
  operator: (string | null | undefined)[],
): string => {
  const dropped = lower([
    ...emailAddresses(to),
    ...operator.filter((a): a is string => !!a),
  ]);
  return participants
    .filter((entry) => {
      const [address] = emailAddresses(entry);
      return !!address && !dropped.has(address.toLowerCase());
    })
    .join(", ");
};

const utf8ToBase64 = (text: string): string => {
  let binary = "";
  for (const byte of new TextEncoder().encode(text)) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
};

// RFC 2047, only when a header carries anything beyond printable ASCII, as
// encoded-words of at most 75 characters (45 UTF-8 bytes), one per folded line.
const encodeHeader = (value: string): string => {
  if (/^[\x20-\x7e]*$/.test(value)) return value;
  const words: string[] = [];
  let chunk = "";
  for (const char of value) {
    if (new TextEncoder().encode(chunk + char).length > 45) {
      words.push(chunk);
      chunk = "";
    }
    chunk += char;
  }
  words.push(chunk);
  return words.map((w) => `=?UTF-8?B?${utf8ToBase64(w)}?=`).join("\r\n ");
};

// One item per folded line, so a long Cc or reference chain stays under RFC 5322's
// 998-character line limit.
const folded = (items: string[], separator: string): string =>
  items.join(`${separator}\r\n `);

/** The RFC 5322 message. Addresses go out bare: agents write To as prose. */
export const buildMime = (
  fields: DraftFields,
  threading: { inReplyTo: string; references: string[] } | null,
): string => {
  const headers = [`To: ${folded(emailAddresses(fields.to), ",")}`];
  const cc = emailAddresses(fields.cc);
  if (cc.length > 0) headers.push(`Cc: ${folded(cc, ",")}`);
  headers.push(`Subject: ${encodeHeader(fields.subject)}`);
  if (threading?.inReplyTo) {
    headers.push(`In-Reply-To: <${threading.inReplyTo}>`);
    headers.push(
      `References: ${folded(
        threading.references.map((r) => `<${r}>`),
        "",
      )}`,
    );
  }
  const body = utf8ToBase64(fields.body.replace(/\r?\n/g, "\r\n"));
  return [
    ...headers,
    "MIME-Version: 1.0",
    'Content-Type: text/plain; charset="UTF-8"',
    "Content-Transfer-Encoding: base64",
    "",
    ...(body.match(/.{1,76}/g) ?? []),
  ].join("\r\n");
};

const base64Url = (text: string): string =>
  utf8ToBase64(text).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const failed = async (what: string, resp: Response): Promise<never> => {
  throw new GmailStatusError(
    resp.status,
    `Gmail could not ${what} (${resp.status}): ${await resp.text()}`,
  );
};

const profileEmail = async (token: string, onRetry?: OnGmailRetry): Promise<string> => {
  const resp = await gmailFetch(`${GMAIL_API_BASE}/profile`, token, onRetry);
  if (!resp.ok) await failed("read your Gmail address", resp);
  const data = (await resp.json()) as { emailAddress?: string };
  return data.emailAddress ?? "";
};

// The operator's own copy of the conversation, if they were on it.
const findThreadId = async (
  messageIds: string[],
  token: string,
  onRetry?: OnGmailRetry,
): Promise<string | null> => {
  for (const id of messageIds.slice(0, MAX_THREAD_LOOKUPS)) {
    const url = new URL(`${GMAIL_API_BASE}/messages`);
    url.searchParams.set("q", `rfc822msgid:${id}`);
    url.searchParams.set("maxResults", "1");
    const resp = await gmailFetch(url.toString(), token, onRetry);
    if (!resp.ok) await failed("search your mailbox for the thread", resp);
    const data = (await resp.json()) as {
      messages?: { id: string; threadId: string }[];
    };
    const hit = data.messages?.[0];
    if (hit) return hit.threadId;
  }
  return null;
};

// Which Gmail draft a card already made, so a second press updates it.
const STORAGE_PREFIX = "inrange-agents.gmail-draft.";

const storedDraftId = (cardId: string, account: string): string | null => {
  if (!cardId) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + cardId);
    const parsed = raw ? (JSON.parse(raw) as { id?: string; account?: string }) : null;
    return parsed?.account === account && parsed.id ? parsed.id : null;
  } catch {
    return null;
  }
};

const storeDraftId = (cardId: string, account: string, id: string): void => {
  if (!cardId) return;
  try {
    window.localStorage.setItem(
      STORAGE_PREFIX + cardId,
      JSON.stringify({ id, account }),
    );
  } catch {
    // Unavailable storage only costs a second draft on a later press.
  }
};

const saveDraft = async (
  message: { raw: string; threadId?: string },
  existing: string | null,
  token: string,
  onRetry?: OnGmailRetry,
): Promise<{ id: string; message: { id: string; threadId?: string } }> => {
  if (existing) {
    const resp = await gmailFetch(
      `${GMAIL_API_BASE}/drafts/${encodeURIComponent(existing)}`,
      token,
      onRetry,
      { method: "PUT", body: JSON.stringify({ id: existing, message }) },
    );
    if (resp.ok) return resp.json();
    // Gone from Gmail (sent or deleted): make a new one.
    if (resp.status !== 404) await failed("update the draft", resp);
  }
  const resp = await gmailFetch(`${GMAIL_API_BASE}/drafts`, token, onRetry, {
    method: "POST",
    body: JSON.stringify({ message }),
  });
  if (!resp.ok) await failed("create the draft", resp);
  return resp.json();
};

/** Create, or update, the card's draft in the operator's Gmail. The token comes
 * first, while the click can still open Google's consent popup; then the thread's
 * context (null for an email that answers none, which makes a new message), and the
 * fields the card holds against it. */
export const saveGmailDraft = async ({
  cardId,
  loadContext,
  fieldsFor,
  onRetry,
}: {
  cardId: string;
  loadContext: () => Promise<ReplyContext | null>;
  fieldsFor: (context: ReplyContext | null) => DraftFields;
  onRetry?: OnGmailRetry;
}): Promise<SavedGmailDraft> => {
  const run = async (token: string): Promise<SavedGmailDraft> => {
    const account = (await profileEmail(token, onRetry)).toLowerCase();
    const context = await loadContext();
    const fields = fieldsFor(context);
    const notCopied = lower([...emailAddresses(fields.to), account]);
    const cc = emailAddresses(fields.cc)
      .filter((a) => !notCopied.has(a.toLowerCase()))
      .join(", ");
    if (emailAddresses(`${fields.to} ${cc}`).length === 0) {
      throw new Error("The email has no address on To or Cc, so no draft was made.");
    }
    // The thread holding the newest message the operator has, asked of Gmail: one
    // conversation can be split across several of their threads. The stored id
    // covers syncs that kept no Message-IDs.
    const held = context?.mailbox_threads.find((m) => m.account === account);
    const threadId = context
      ? ((await findThreadId(context.message_ids, token, onRetry)) ??
        held?.thread_id ??
        null)
      : null;
    const raw = base64Url(
      buildMime(
        { ...fields, cc },
        context
          ? { inReplyTo: context.in_reply_to, references: context.references }
          : null,
      ),
    );
    const saved = await saveDraft(
      threadId ? { raw, threadId } : { raw },
      storedDraftId(cardId, account),
      token,
      onRetry,
    );
    storeDraftId(cardId, account, saved.id);
    const placement: DraftPlacement = !context
      ? "new"
      : threadId
        ? saved.message.threadId === threadId
          ? "thread"
          : "split"
        : context.message_ids.length === 0
          ? "no_ids"
          : "not_on_thread";
    return { draftId: saved.id, messageId: saved.message.id, account, placement };
  };

  await ensureGsiLoaded();
  const token = await requestGoogleAccessToken(DRAFT_SCOPES);
  try {
    return await run(token);
  } catch (err) {
    if (!(err instanceof GmailStatusError) || err.status !== 401) throw err;
    return run(await refreshGoogleAccessToken(DRAFT_SCOPES));
  }
};
